import React from 'react'
import {Box, Flex, Grid, Text, useDisclosure, useStyleConfig} from '@chakra-ui/react'

// import {mockCompareProducts, gridHeadingsConfig} from '../../components/product-compare/mock-data'
import {useCompare} from '../../context/compare'
import ProductCompareGrid from '../../components/product-compare/partials/product-compare-grid'
import {AddIcon, MinusIcon} from '../../components/icons'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useArrayOfSpecifications} from '../../utils/specifications-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import PropTypes from 'prop-types'

const getProductIdsFromCompareItems = (compareItems) => {
    return compareItems.map((item) => item.productId)
}

const buildCategoryTableData = (accordionData) => {
    if (!accordionData || accordionData.length === 0) {
        return []
    }

    // Get all unique category titles from all products
    const allCategories = [
        ...new Set(
            accordionData
                .flat()
                .map((item) => item.accordion_title)
                .filter(Boolean),
        ),
    ]

    // Build table data for each category, showing all specs even if not in all products
    const result = allCategories.map((categoryLabel) => {
        const allValueNames = [
            ...new Set(
                accordionData
                    .map((array) => {
                        const cat = array.find((item) => item.accordion_title === categoryLabel)
                        return cat && Array.isArray(cat.values)
                            ? cat.values.map((v) => v.value_name)
                            : []
                    })
                    .flat()
                    .filter(Boolean),
            ),
        ]

        const rows = allValueNames.map((value_name) => ({
            id: value_name,
            label: value_name,
            values: accordionData.map((array) => {
                const cat = array.find((item) => item.accordion_title === categoryLabel)
                if (!cat || !Array.isArray(cat.values)) return '-'
                const valObj = cat.values.find((v) => v.value_name === value_name)
                return valObj ? valObj.value ?? '-' : '-'
            }),
        }))

        return {categoryLabel, rows}
    })

    return result
}

const CompareTable = ({dataToDisplay, categoryLabel, numOfProducts}) => {
    const {isOpen, onToggle} = useDisclosure({defaultIsOpen: true})
    const styles = useStyleConfig('ProductComparePlp')

    return (
        <Box overflowX="auto" w="100%">
            <Flex {...styles.compareTableHeader} onClick={onToggle}>
                <Text {...styles.compareTableHeaderLabel}>{categoryLabel}</Text>
                {isOpen ? (
                    <MinusIcon boxSize="iconsSize.medium" />
                ) : (
                    <AddIcon boxSize="iconsSize.medium" />
                )}
            </Flex>
            {isOpen && (
                <ProductCompareGrid
                    gridData={dataToDisplay}
                    numOfProducts={numOfProducts}
                    colorlessHeaders
                    highlightDifferences
                />
            )}
        </Box>
    )
}

CompareTable.propTypes = {
    dataToDisplay: PropTypes.array,
    categoryLabel: PropTypes.string,
    numOfProducts: PropTypes.number,
}

const CompareTables = () => {
    const {
        state: {compareItems},
    } = useCompare()

    const styles = useStyleConfig('ProductComparePlp')
    const {locale} = useMultiSite()
    const {currency} = useCurrency()

    const productIds = getProductIdsFromCompareItems(compareItems)

    const {data: productIdsData} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                locale: locale.id,
                currency,
                c_includeTechSpecs: true,
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'compare-table-index',
            },
        },
        {
            enabled: productIds?.length > 0,
            keepPreviousData: true,
        },
    )

    const accordionData = useArrayOfSpecifications(productIdsData?.data)
    const numOfProducts = productIdsData?.data?.length || 0
    const dataToDisplay = buildCategoryTableData(accordionData)

    return (
        <Grid {...styles.compareTableGrid}>
            {dataToDisplay.map((table) => (
                <CompareTable
                    key={table.categoryLabel}
                    dataToDisplay={table.rows}
                    numOfProducts={numOfProducts}
                    categoryLabel={table.categoryLabel}
                />
            ))}
        </Grid>
    )
}

export default CompareTables
